import debounce from 'hs-lodash/debounce';
import { useEffect, useCallback, useState, useMemo } from 'react';
import { useMutation } from 'data-fetching-client';
import { UPSERT_DRAFT_MUTATION, DELETE_DRAFT_MUTATION } from '../../../../__generated__/chirp/com/hubspot/cv/threads/drafts/rpc/DraftsFrontendService.dfc';
import { deserializeCommonReplyDraft } from '../operators/deserializeCommonReplyDraft';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { useSelector } from 'react-redux';
import { getIsUngatedForPersistedDrafts } from 'conversations-thread-data/auth/public/selectors';
import { serializeCommonReplyDraft } from '../operators/serializeCommonReplyDraft';
import { getEditorState } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { convertToPlaintext } from 'hubspot-prosemirror/prosemirror/ProsemirrorUtils';
import { usePersistentDraftsQuery } from './usePersistentDraftsQuery';
import { trackEvent } from '../../../usage-tracking/clients/usageTracker';
import { composerDraftCreated } from '../../../usage-tracking/events/editorInteractions';
import { getDraftAttachmentTypename } from '../operators/getDraftAttachmentTypenames';
export const usePersistedCommonReplyDraft = (genericChannelId, idType, idNumber, saveTimeout, onDraftChange, __replyId) => {
  const [hasSessionSavedDraft, setHasSessionSavedDraft] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const isUngatedForPersistedDrafts = useSelector(getIsUngatedForPersistedDrafts);
  const {
    loadingCurrentDrafts,
    currentDrafts,
    refetch
  } = usePersistentDraftsQuery({
    idType,
    idNumber,
    isUngatedForPersistedDrafts
  });
  useEffect(() => {
    if (isUngatedForPersistedDrafts) {
      setIsRefetching(true);
      refetch().finally(() => {
        setIsRefetching(false);
      }).catch(error => {
        reportError({
          error: new Error('Error refetching drafts'),
          extra: {
            error
          }
        });
      });
    }
  }, [genericChannelId, refetch, isUngatedForPersistedDrafts, idNumber]);
  const [draftSaveFailed, setDraftSaveFailed] = useState(false);
  const [localDraft, setLocalDraft] = useState();
  const [saveDraft] = useMutation(UPSERT_DRAFT_MUTATION);
  const [deleteDraft] = useMutation(DELETE_DRAFT_MUTATION);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(debounce((replyToSave, retryCount = 0, maxRetries = 3) => {
    if (!loadingCurrentDrafts && genericChannelId) {
      var _replyToSave$commonMe, _replyToSave$commonMe2;
      if (idType === 'WORKSPACE_ID' && !((_replyToSave$commonMe = replyToSave.commonMessage) !== null && _replyToSave$commonMe !== void 0 && _replyToSave$commonMe.recipients.size)) {
        return;
      }
      const editorState = getEditorState(replyToSave);
      const plainTextContent = convertToPlaintext(editorState, {
        allowPlaceholderImages: true
      });
      const fileAttachments = (_replyToSave$commonMe2 = replyToSave.commonMessage) === null || _replyToSave$commonMe2 === void 0 ? void 0 : _replyToSave$commonMe2.attachments.filter(attachment => attachment['@type'] === 'FILES' && attachment['fileIds'] && attachment['fileIds'].size > 0);
      if (plainTextContent.trim().length === 0 && (!fileAttachments || fileAttachments.size === 0)) {
        return;
      }
      saveDraft({
        variables: {
          idType,
          id: idNumber,
          genericChannelId,
          jsonBlob: serializeCommonReplyDraft(replyToSave),
          timestamp: Date.now(),
          plainTextContent,
          attachments: replyToSave.commonMessage ? replyToSave.commonMessage.attachments.toJS().map(attachment => Object.assign({}, attachment, {
            __typename: getDraftAttachmentTypename(attachment['@type'])
          })) : []
        }
      }).then(() => {
        onDraftChange === null || onDraftChange === void 0 || onDraftChange({
          text: plainTextContent
        });
        setDraftSaveFailed(false);
        setHasSessionSavedDraft(true);
      }).catch(error => {
        setDraftSaveFailed(true);
        if (retryCount < maxRetries) {
          // Retry with exponential backoff
          setTimeout(() => {
            debouncedSave(replyToSave, retryCount + 1, maxRetries);
          }, 1000 * Math.pow(2, retryCount));
        } else {
          reportError({
            error: new Error('Error saving draft after max retries'),
            extra: {
              error
            }
          });
        }
      });
    }
  }, saveTimeout), [saveTimeout, genericChannelId, idNumber, loadingCurrentDrafts, saveDraft, idType]);
  useEffect(() => {
    debouncedSave.flush();
    return debouncedSave.flush;
  }, [debouncedSave]);
  useEffect(() => {
    var _currentDrafts$getDra;
    const drafts = currentDrafts === null || currentDrafts === void 0 || (_currentDrafts$getDra = currentDrafts.getDrafts) === null || _currentDrafts$getDra === void 0 ? void 0 : _currentDrafts$getDra.drafts;
    if (!drafts) return;
    if (idType === 'WORKSPACE_ID' && drafts !== null && drafts !== void 0 && drafts.length) {
      setLocalDraft(drafts[0]);
    } else {
      setLocalDraft(drafts === null || drafts === void 0 ? void 0 : drafts.find(d => d.genericChannelId === genericChannelId));
    }
  }, [currentDrafts, genericChannelId, idType]);

  // track when a draft is saved and then abandoned on unmount and thus "creating" a new draft
  useEffect(() => {
    if (hasSessionSavedDraft) {
      return () => {
        var _genericChannelId$toS;
        return trackEvent(composerDraftCreated({
          channel: (_genericChannelId$toS = genericChannelId === null || genericChannelId === void 0 ? void 0 : genericChannelId.toString()) !== null && _genericChannelId$toS !== void 0 ? _genericChannelId$toS : ''
        }));
      };
    }
  }, [hasSessionSavedDraft, genericChannelId, idType, idNumber]);
  const clear = useCallback(() => {
    debouncedSave.cancel();
    if (genericChannelId) {
      deleteDraft({
        variables: {
          idType,
          id: idNumber,
          genericChannelId,
          timestamp: Date.now()
        }
      }).then(() => {
        setLocalDraft(undefined);
        setHasSessionSavedDraft(false);
        onDraftChange === null || onDraftChange === void 0 || onDraftChange({
          text: ''
        });
      }).catch(error => {
        reportError({
          error: new Error('Error deleting draft'),
          extra: {
            error
          }
        });
      });
      setLocalDraft(undefined);
      onDraftChange === null || onDraftChange === void 0 || onDraftChange({
        text: ''
      });
    }
  }, [debouncedSave, deleteDraft, genericChannelId, idNumber, idType, onDraftChange]);
  const deserializedDraft = useMemo(() => localDraft && localDraft.jsonBlob ? deserializeCommonReplyDraft(localDraft.jsonBlob) : null, [localDraft]);
  return {
    draft: deserializedDraft,
    saveDraft: debouncedSave,
    clearDraft: clear,
    // It's important this uses the useQuery loading state since it has a timeout of 4s and
    // thus we won't block the UI if the draft ever fails to load in a reasonable amount of time
    hasLoadedDrafts: !loadingCurrentDrafts && !isRefetching,
    draftSaveFailed
  };
};